// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xScreen } from "@ces/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import React from "react";
import { css } from "styled-components";

const enum fields {
    label = "label",
    description = "description",
    customCSS = "customCSS",
    showEmailField = "showEmailField",
    backgroundImage = "backgroundImage",
    mediumBackgroundImage = "mediumBackgroundImage",
    smallBackgroundImage = "smallBackgroundImage",
    buttonLink = "buttonLink",
    openInNewTab = "openInNewTab",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.description]: string;
        [fields.customCSS]: string;
        [fields.showEmailField]: boolean;
        [fields.backgroundImage]: string;
        [fields.mediumBackgroundImage]: string;
        [fields.smallBackgroundImage]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
    };
}

type Props = OwnProps;

export interface MailingListStyles {
    container?: InjectableCss;
    descriptionText?: TypographyProps;
    button?: ButtonPresentationProps;
    clickable?: ClickableProps;
}

export const mailingListStyles: MailingListStyles = {};

const defaultCustomCss = `.main-grid-container{
}

.title-grid-item{
}

.title{
}

.description-grid-item{
}

.description-text{
}

.email-grid-item{
}

.email-text-field{
}

.email-button{
}

.disclaimer-grid-item{
}

.disclaimer-text{
}`;

const styles = mailingListStyles;

export const MailingList = ({ fields }: Props) => {
    const buttonLink = useGetLink(fields.buttonLink);
    const imageFallback = fields.smallBackgroundImage || fields.mediumBackgroundImage || fields.backgroundImage;

    const customCssWrapper = {
        css: css`
            background-image: url("${fields.backgroundImage}");
            background-size: cover;
            position: relative;
            width: 100%;
            max-width: 882px;
            height: 82px;
            
            top: -44px;
            margin: 0 auto;
            clip-path: polygon(22px 0%, 862px 0%, 100% 50%, 862px 100%, 22px 100%, 0% 50%);
            display: flex;
            align-items: center;
            justify-content: center;
            ${xScreen("lte", "md")} {
                gap: 20px;
                clip-path: unset;
                max-width: unset;
                position: unset;
                transform: unset;
                height: auto;
                min-height: 82px;
                padding: 20px 16px;
                background-image: url("${fields.mediumBackgroundImage || fields.backgroundImage}");
            }
            ${xScreen("lte", "xs")} {
                background-image: url("${imageFallback}");
            }
            ${fields.customCSS};
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper {...styles.container}>
                <Typography {...styles.descriptionText}>{fields.description}</Typography>
                <Clickable {...styles.clickable} href={buttonLink.url} target={fields.openInNewTab ? "_blank" : ""}>
                    <Button {...styles.button}>{fields.label}</Button>
                </Clickable>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: MailingList,
    definition: {
        group: "Basic",
        icon: "bell",
        allowedContexts: ["Footer"],
        fieldDefinitions: [
            {
                name: fields.backgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                tab: basicTab,
                isRequired: true,
            },
            {
                name: fields.mediumBackgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.smallBackgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "TextField",
                defaultValue: "Keep up to date with the latest products, special offers and news.",
                fieldType: "Translatable",
                tab: basicTab,
            },

            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "Join Our Mailing List",
                fieldType: "Translatable",
                tab: basicTab,
            },

            {
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    type: "Url",
                    value: "",
                },
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.openInNewTab,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
